import { execFile } from 'node:child_process';
import { randomBytes } from 'node:crypto';
import { promisify } from 'node:util';
import { getDockerCmd } from '../utils/docker-cmd.js';
import { getLogger } from '../utils/logger.js';
import {
  DockerRunnerError,
  pullImage,
  removeContainer,
  appendEnvFlags,
  execEnvForSecrets,
  SENSITIVE_ENV_KEYS,
} from './docker-runner.js';
import { getEmbedConfig } from './embed-config.js';

const execFileAsync = promisify(execFile);

/**
 * Docker network + AWS-published metadata-endpoints sidecar lifecycle for
 * `cdkl run-task`. The sidecar (a small Go binary maintained by
 * awslabs) is started at `169.254.170.2` on the per-task docker network so
 * containers can hit `http://169.254.170.2/v4/<container-id>` for task
 * metadata AND `AWS_CONTAINER_CREDENTIALS_RELATIVE_URI=/role/<role-arn>`
 * for IAM task-role credentials. cdk-local does NOT re-implement the sidecar
 * — pulling the AWS-published image keeps cdk-local in lock-step with whatever
 * ECS-Agent fidelity AWS chooses to provide.
 */

/** AWS-published sidecar image (latest tag). amd64 is the only image AWS ships. */
export const METADATA_ENDPOINT_IMAGE = 'amazon/amazon-ecs-local-container-endpoints:latest-amd64';

/**
 * Default well-known IP for the ECS local-container-endpoints sidecar —
 * matches the documented AWS task-metadata endpoint address. Containers
 * inject `ECS_CONTAINER_METADATA_URI_V4=http://169.254.170.2/v4/<id>`
 * to reach it. `cdkl run-task` keeps this verbatim; `cdk-local
 * start-service` creates ONE shared network at CLI startup (design
 * § 5 Option A) — the shared sidecar lives at `169.254.171.2` (see
 * `SHARED_SVC_SUBNET_OCTET` below), one octet up so the two CLI
 * variants can run on the same host without bridge-pool collision.
 */
export const METADATA_ENDPOINT_IP = '169.254.170.2';

/** Default subnet — used when no `subnetOctet` override is supplied. */
const DEFAULT_METADATA_ENDPOINT_SUBNET = '169.254.170.0/24';

/**
 * Pure-functional subnet allocator. `cdkl run-task` uses the
 * default subnet (octet 170); `cdkl start-service` uses a SINGLE
 * shared network at the fixed octet `SHARED_SVC_SUBNET_OCTET = 171`
 * (one octet up from run-task so the two CLI variants can coexist on
 * the same host). The link-local 169.254.0.0/16 space is reserved
 * AWS-wide for cloud metadata so collisions with user workloads are
 * unlikely, but the fixed /24 still keeps docker's `--subnet`
 * allocator from rejecting "Pool overlaps".
 *
 * `subnetOctet` is the second-from-last byte of the network: 170 →
 * 169.254.170.0/24 (default), 171 → 169.254.171.0/24, etc. Valid
 * range is 1..254 — exported here so callers keep the CIDR /
 * sidecar-IP derivation in one place.
 */
export function buildEndpointSubnet(subnetOctet: number): {
  cidr: string;
  sidecarIp: string;
} {
  if (subnetOctet < 1 || subnetOctet > 254 || !Number.isInteger(subnetOctet)) {
    throw new Error(
      `buildEndpointSubnet: subnetOctet must be an integer in 1..254 (got ${subnetOctet}).`
    );
  }
  return {
    cidr: `169.254.${subnetOctet}.0/24`,
    sidecarIp: `169.254.${subnetOctet}.2`,
  };
}

export interface TaskNetwork {
  /** Generated docker network name (`<prefix>-task-<rand>` or `<prefix>-svc-<rand>` for shared). */
  networkName: string;
  /** Container id of the metadata-endpoints sidecar. Cleaned up at teardown. */
  sidecarContainerId: string;
  /**
   * Resolved sidecar IP for THIS network instance. `cdkl run-task`
   * sees 169.254.170.2 (the default); `cdkl start-service` shares
   * a single network across every service in the run at
   * `169.254.171.2`. Containers' `ECS_CONTAINER_METADATA_URI_V4` is
   * derived from this so each replica's containers hit the right
   * sidecar regardless of which network they joined.
   */
  sidecarIp: string;
  /**
   * When true, the network + sidecar are owned by the caller (the CLI
   * created them once and reuses across every task / replica boot in
   * the run) and `cleanupEcsRun()` MUST NOT teardown — only the caller
   * tears down at the end of the CLI lifecycle. When false / undefined,
   * the task runner owns the lifecycle (the pre-existing
   * `cdkl run-task` shape: one network per task, torn down
   * with the task).
   */
  ownedByCaller?: boolean;
}

export interface CreateTaskNetworkOptions {
  /**
   * Docker network name prefix. Default `cdkl`; the runner injects
   * the CLI's `--cluster <name>`. The full name is `<prefix>-task-<rand>`.
   */
  prefix?: string;
  /**
   * When set, the sidecar receives `AWS_ACCESS_KEY_ID` /
   * `AWS_SECRET_ACCESS_KEY` / `AWS_SESSION_TOKEN` env vars so its
   * `/role/<role-arn>` endpoint serves these creds to the user
   * containers. When unset, the sidecar falls back to its default
   * credential chain (typically empty — the user containers will get
   * 4xx from the credentials endpoint, mimicking IAM-misconfigured prod).
   */
  credentials?: {
    accessKeyId: string;
    secretAccessKey: string;
    sessionToken?: string;
  };
  /** `--cluster <name>` value. Forwarded to the sidecar's `CLUSTER` env. */
  cluster?: string;
  /** Skip `docker pull <sidecar>`. */
  skipPull?: boolean;
  /**
   * Optional second-from-last octet of the link-local /24 subnet
   * (1..254). Default 170 (the AWS-documented metadata-endpoint subnet).
   * `cdkl start-service` walks this value per replica so
   * concurrent docker networks don't collide on the same /24 range.
   */
  subnetOctet?: number;
}

/**
 * Subnet octet for the shared-service docker network used by
 * `cdkl start-service`. One octet up from `cdkl run-task`'s
 * default (170 → 171) so the two CLI variants can run on the same host
 * without docker rejecting the second `--subnet`. The shared-service
 * network reuses the same `createTaskNetwork` machinery; the sidecar at
 * `169.254.171.2` serves the same metadata-endpoint API to every
 * container that joins this one network.
 */
export const SHARED_SVC_SUBNET_OCTET = 171;

/**
 * Create the one shared docker network + metadata-endpoints sidecar
 * used by every service-replica boot in a single
 * `cdkl start-service` invocation. This is design doc § 5
 * Option A — one network per CLI invocation instead of one network
 * per task — so peer services can reach each other by IP / network
 * alias without docker `--network connect` choreography (Option B,
 * rejected in design § 5 as "unwieldy and racy"). The returned
 * `TaskNetwork` carries `ownedByCaller: true` so `cleanupEcsRun()`
 * (called per replica by the service runner) does NOT teardown — the
 * CLI tears down ONCE at the end of the run.
 */
export async function createSharedSvcNetwork(
  options: Omit<CreateTaskNetworkOptions, 'subnetOctet'> = {}
): Promise<TaskNetwork> {
  const prefix = options.prefix ?? getEmbedConfig().resourceNamePrefix;
  // Reclaim any `<prefix>-svc-*` network left behind by a previous
  // `start-service` run that was interrupted (Ctrl-C / crash) before its
  // teardown. Because start-service pins a SINGLE fixed subnet
  // (SHARED_SVC_SUBNET_OCTET), a leaked network blocks every later run with
  // a "Pool overlaps" error that never self-heals — so sweep BEFORE the
  // fixed-subnet create rather than only surfacing the error afterwards.
  await sweepOrphanedSvcNetworks(prefix);
  const suffix = randomBytes(4).toString('hex');
  const networkName = `${prefix}-svc-${suffix}`;
  const { cidr, sidecarIp } = buildEndpointSubnet(SHARED_SVC_SUBNET_OCTET);
  const sidecarContainerId = await createNetworkAndSidecar({
    networkName,
    cidr,
    sidecarIp,
    skipPull: options.skipPull ?? false,
    ...(options.credentials !== undefined ? { credentials: options.credentials } : {}),
    ...(options.cluster !== undefined ? { cluster: options.cluster } : {}),
  });
  return { networkName, sidecarContainerId, sidecarIp, ownedByCaller: true };
}

/**
 * Startup orphan sweep for the shared `cdkl start-service` network
 * (Issue #93, design Option 1). When a `start-service` run is interrupted
 * before its end-of-run teardown, the shared `<prefix>-svc-<rand>` network
 * and its `<prefix>-svc-<rand>-metadata` sidecar LEAK. Because
 * `start-service` pins a single fixed subnet (`SHARED_SVC_SUBNET_OCTET`),
 * the next run's `docker network create` always fails with
 * "Pool overlaps with other one on this address space" — a state that,
 * unlike a port conflict, never self-heals. This sweep detects and removes
 * leaked `<prefix>-svc-*` networks that have NO live owner before the next
 * run re-creates the network.
 *
 * Classification heuristic: a `<prefix>-svc-*` network is ORPHANED when its
 * only attached container is its own `<name>-metadata` sidecar (or it has
 * zero attached containers). A network that still has a non-metadata
 * (user replica) container attached is a live concurrent run and is LEFT
 * untouched. Caveat: "only the metadata sidecar attached ⇒ orphan" can
 * misclassify a network in the sub-second window between sidecar start and
 * the first replica attach — so a second `start-service` launched in that
 * window could reclaim a concurrently-starting run's network out from under
 * it. This is an accepted limitation, not a benign one: start-service pins a
 * single fixed subnet, so two concurrent same-prefix runs were never
 * supported (the second run's `docker network create` already fails with
 * "Pool overlaps"). The sweep therefore cannot regress a
 * previously-working scenario.
 *
 * Resilient by design: a `docker network ls` / `inspect` failure must not
 * abort the run — it logs at debug and skips, matching the idempotent
 * teardown style in this file. Returns the list of swept network names.
 */
export async function sweepOrphanedSvcNetworks(prefix: string): Promise<string[]> {
  const logger = getLogger().child('ecs-network');
  const filter = `${prefix}-svc-`;
  let names: string[];
  try {
    const { stdout } = await execFileAsync(getDockerCmd(), [
      'network',
      'ls',
      '--filter',
      `name=${filter}`,
      '--format',
      '{{.Name}}',
    ]);
    names = stdout
      .split('\n')
      .map((n) => n.trim())
      .filter((n) => n.length > 0);
  } catch (err) {
    const e = err as { stderr?: string; message?: string };
    logger.debug(`docker network ls (sweep) failed: ${e.stderr || e.message || String(err)}`);
    return [];
  }

  const swept: string[] = [];
  for (const name of names) {
    let attached: string[];
    try {
      const { stdout } = await execFileAsync(getDockerCmd(), [
        'network',
        'inspect',
        name,
        '--format',
        '{{range .Containers}}{{.Name}} {{end}}',
      ]);
      attached = stdout
        .split(/\s+/)
        .map((c) => c.trim())
        .filter((c) => c.length > 0);
    } catch (err) {
      const e = err as { stderr?: string; message?: string };
      logger.debug(
        `docker network inspect ${name} (sweep) failed: ${e.stderr || e.message || String(err)}`
      );
      continue;
    }

    const sidecarName = `${name}-metadata`;
    const hasLiveOwner = attached.some((c) => c !== sidecarName);
    if (hasLiveOwner) {
      // A user replica is still attached — this is a live concurrent run.
      continue;
    }

    logger.info(`Reclaiming orphaned shared network ${name} (no live owner)...`);
    await removeContainer(sidecarName);
    await destroyNetworkOnly(name);
    swept.push(name);
  }
  return swept;
}

/**
 * Internal helper shared by `createTaskNetwork` (per-task) and
 * `createSharedSvcNetwork` (per-CLI-run). Creates the docker network,
 * pulls the sidecar image, and starts the sidecar at the documented
 * IP. Throws `DockerRunnerError` with a hint when the network already
 * exists (the typical "leftover from previous run" path).
 */
async function createNetworkAndSidecar(args: {
  networkName: string;
  cidr: string;
  sidecarIp: string;
  credentials?: CreateTaskNetworkOptions['credentials'];
  cluster?: string;
  skipPull: boolean;
}): Promise<string> {
  const logger = getLogger().child('ecs-network');
  const { networkName, cidr, sidecarIp, credentials, cluster, skipPull } = args;

  await pullImage(METADATA_ENDPOINT_IMAGE, skipPull);

  logger.info(`Creating docker network ${networkName} (subnet ${cidr})...`);
  try {
    await execFileAsync(getDockerCmd(), [
      'network',
      'create',
      '--driver',
      'bridge',
      '--subnet',
      cidr,
      networkName,
    ]);
  } catch (err) {
    const e = err as { stderr?: string; message?: string };
    throw new DockerRunnerError(
      `docker network create failed: ${e.stderr?.trim() || e.message || String(err)}. ` +
        `Hint: another ${getEmbedConfig().productName} run may already own subnet ${cidr}; wait for it to ` +
        'finish, or remove the leftover network with `docker network ls` + ' +
        `\`docker network rm\`. \`${getEmbedConfig().cliName} start-service\` shares one network ` +
        `across every service in the run; bare \`${getEmbedConfig().cliName} run-task\` uses a ` +
        'per-task network so only one run can be active at a time.'
    );
  }

  const sidecarArgs: string[] = [
    'run',
    '-d',
    '--rm',
    '--name',
    `${networkName}-metadata`,
    '--network',
    networkName,
    '--ip',
    sidecarIp,
  ];
  const sidecarEnv: Record<string, string> = {};
  if (credentials) {
    sidecarEnv['AWS_ACCESS_KEY_ID'] = credentials.accessKeyId;
    sidecarEnv['AWS_SECRET_ACCESS_KEY'] = credentials.secretAccessKey;
    if (credentials.sessionToken) {
      sidecarEnv['AWS_SESSION_TOKEN'] = credentials.sessionToken;
    }
  }
  if (cluster) sidecarEnv['CLUSTER'] = cluster;
  // AWS credentials served by the metadata sidecar route through docker's
  // value-from-process-env form (`-e KEY`) so they never appear in argv;
  // CLUSTER (non-secret) keeps the inline `-e KEY=VALUE` form.
  const sidecarPassthroughEnv = appendEnvFlags(sidecarArgs, sidecarEnv, SENSITIVE_ENV_KEYS);
  sidecarArgs.push(METADATA_ENDPOINT_IMAGE);

  logger.info(`Starting ECS local-container-endpoints sidecar at ${sidecarIp}...`);
  try {
    const { stdout } = await execFileAsync(getDockerCmd(), sidecarArgs, {
      maxBuffer: 10 * 1024 * 1024,
      ...execEnvForSecrets(sidecarPassthroughEnv),
    });
    return stdout.trim();
  } catch (err) {
    await destroyNetworkOnly(networkName);
    const e = err as { stderr?: string; message?: string };
    throw new DockerRunnerError(
      `Failed to start metadata-endpoints sidecar: ${e.stderr?.trim() || e.message || String(err)}`
    );
  }
}

/**
 * Create the per-task docker network + start the metadata-endpoints
 * sidecar. The sidecar must come up at the well-known address BEFORE any
 * user container starts so the `AWS_CONTAINER_CREDENTIALS_RELATIVE_URI`
 * lookup at container start doesn't race.
 */
export async function createTaskNetwork(
  options: CreateTaskNetworkOptions = {}
): Promise<TaskNetwork> {
  const prefix = options.prefix ?? getEmbedConfig().resourceNamePrefix;
  const suffix = randomBytes(4).toString('hex');
  const networkName = `${prefix}-task-${suffix}`;
  const { cidr, sidecarIp } =
    options.subnetOctet === undefined
      ? { cidr: DEFAULT_METADATA_ENDPOINT_SUBNET, sidecarIp: METADATA_ENDPOINT_IP }
      : buildEndpointSubnet(options.subnetOctet);
  const sidecarContainerId = await createNetworkAndSidecar({
    networkName,
    cidr,
    sidecarIp,
    skipPull: options.skipPull ?? false,
    ...(options.credentials !== undefined ? { credentials: options.credentials } : {}),
    ...(options.cluster !== undefined ? { cluster: options.cluster } : {}),
  });
  return { networkName, sidecarContainerId, sidecarIp };
}

/**
 * Build the env var entries every user container needs so its AWS SDK
 * picks up the sidecar. `<container-id>` is replaced by the actual docker
 * id post-`run` — at this point we use the container name as a stable
 * proxy since the metadata endpoint accepts a name lookup.
 *
 * `roleArn` is the optional task role ARN. When set, the credentials
 * endpoint path bakes it in so AWS SDK clients pull the assumed creds
 * automatically; when unset, the path is omitted (containers fall back
 * to whichever credentials AWS SDK chains find).
 */
export function buildMetadataEnv(opts: {
  containerName: string;
  roleArn?: string;
  region?: string;
  /**
   * Sidecar IP for this task's network. Defaults to the AWS-documented
   * `169.254.170.2` so `cdkl run-task` keeps the canonical shape.
   * `cdkl start-service` passes the per-replica IP allocated by
   * `buildEndpointSubnet(subnetOctet)` so each replica's containers
   * resolve their own sidecar (not a sibling replica's).
   */
  sidecarIp?: string;
}): Record<string, string> {
  const ip = opts.sidecarIp ?? METADATA_ENDPOINT_IP;
  const env: Record<string, string> = {
    ECS_CONTAINER_METADATA_URI_V4: `http://${ip}/v4/${opts.containerName}`,
    ECS_CONTAINER_METADATA_URI: `http://${ip}/v3/${opts.containerName}`,
  };
  if (opts.roleArn) {
    env['AWS_CONTAINER_CREDENTIALS_RELATIVE_URI'] = `/role/${encodeURIComponent(opts.roleArn)}`;
  }
  if (opts.region) env['AWS_REGION'] = opts.region;
  return env;
}

/**
 * Tear down the metadata-endpoints sidecar + the docker network. Idempotent
 * — `docker rm -f` and `docker network rm` both swallow not-found errors
 * by design, and the function logs at debug instead of throwing.
 */
export async function destroyTaskNetwork(net: TaskNetwork | undefined): Promise<void> {
  if (!net) return;
  await removeContainer(net.sidecarContainerId);
  await destroyNetworkOnly(net.networkName);
}

async function destroyNetworkOnly(networkName: string): Promise<void> {
  if (!networkName) return;
  const logger = getLogger().child('ecs-network');
  try {
    await execFileAsync(getDockerCmd(), ['network', 'rm', networkName]);
    logger.debug(`Removed docker network ${networkName}`);
  } catch (err) {
    const e = err as { stderr?: string; message?: string };
    logger.debug(
      `docker network rm ${networkName} failed: ${e.stderr || e.message || String(err)}`
    );
  }
}
